import { nanoid } from 'common/util/random'
import Compressor from 'compressorjs'
import { last } from 'lodash'
import { getSupabaseBrowserClient } from 'web/lib/supabase/client'

const PUBLIC_BUCKET = 'public-images'
const PRIVATE_BUCKET = 'private-documents'

export const uploadPublicImage = async (
  username: string,
  file: File,
  prefix?: string,
  onProgress?: (progress: number, isRunning: boolean) => void
) => {
  const ext = last(file.name.split('.'))
  const filename = `${nanoid(10)}.${ext}`
  const path = `user-images/${username}${prefix ? '/' + prefix : ''}/${filename}`
  return await uploadToSupabase(PUBLIC_BUCKET, path, file, onProgress)
}

export const uploadPrivateImage = async (
  userId: string,
  file: File,
  fileName: string,
  onProgress?: (progress: number, isRunning: boolean) => void
) => {
  const path = `private-images/${userId}/${fileName}`
  return await uploadToSupabase(PRIVATE_BUCKET, path, file, onProgress)
}

async function uploadToSupabase(
  bucket: string,
  path: string,
  file: File,
  onProgress?: (progress: number, isRunning: boolean) => void
): Promise<string> {
  if (file.size > 20 * 1024 ** 2) {
    throw new Error('File is over 20 MB')
  }

  if (file.type === 'application/pdf' && file.size > 2 * 1024 ** 2) {
    throw new Error('PDF file is over 2MB, please submit a smaller file.')
  }

  // Compress images > 1MB
  if (file.type.startsWith('image/') && file.size > 1024 ** 2) {
    file = await new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.6,
        maxHeight: 1920,
        maxWidth: 1920,
        convertSize: 1000000,
        success: (result: File) => resolve(result),
        error: (error) => reject(error.message),
      })
    })
  }

  if (onProgress) onProgress(0, true)

  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: '31536000', // 1 year
      upsert: false,
    })

  if (error) throw new Error(error.message)

  if (onProgress) onProgress(1, false)

  // Get public URL for public bucket
  if (bucket === PUBLIC_BUCKET) {
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(data.path)
    return urlData.publicUrl
  }

  // For private bucket, create a signed URL (valid for 1 year)
  const { data: signedData, error: signedError } = await supabase.storage
    .from(bucket)
    .createSignedUrl(data.path, 60 * 60 * 24 * 365) // 1 year

  if (signedError) throw new Error(signedError.message)
  return signedData.signedUrl
}
